import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Search, Calendar, MessageCircle, Star, Shield, 
  Heart, Users, ChevronRight, Play, Headphones,
  CheckCircle2, Menu, X, Award, Clock, VolumeX, Volume2, MapPin, Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { API } from "../utils/api";

// SEO helper
const updatePageMeta = (title, description) => {
  document.title = title;
  const metaDesc = document.querySelector('meta[name="description"]');
  if (metaDesc) metaDesc.setAttribute('content', description);
};

// Brand Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_4a6e65b4-dfc4-4116-a52d-9af75b9ab8f4/artifacts/dhbjnlbf_btr%20logo.png";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_emergent-website-3/artifacts/6i1oj5oc_BTR_Health_favicon__2_-removebg-preview.png";
const MARKETING_PHOTO_1 = "https://customer-assets.emergentagent.com/job_16af4fc2-6f73-41bb-acec-700dbc21cc51/artifacts/zln2fvov_Copy%20of%20My%20Kind%20of%20Independnce%20%283%29.png";
const MARKETING_PHOTO_2 = "https://customer-assets.emergentagent.com/job_16af4fc2-6f73-41bb-acec-700dbc21cc51/artifacts/zln2fvov_Copy%20of%20My%20Kind%20of%20Independnce%20%283%29.png";
const MARKETING_PHOTO_3 = "https://customer-assets.emergentagent.com/job_16af4fc2-6f73-41bb-acec-700dbc21cc51/artifacts/zln2fvov_Copy%20of%20My%20Kind%20of%20Independnce%20%283%29.png";
const MARKETING_PHOTO_4 = "https://customer-assets.emergentagent.com/job_16af4fc2-6f73-41bb-acec-700dbc21cc51/artifacts/wvrj0moo_1.png";
const MARKETING_PHOTO_5 = "https://customer-assets.emergentagent.com/job_16af4fc2-6f73-41bb-acec-700dbc21cc51/artifacts/2q6kb99s_Copy%20of%20My%20Kind%20of%20Independnce%20%282%29.png";
const BRAND_VIDEO = "https://customer-assets.emergentagent.com/job_emergent-website-3/artifacts/gg19f9wq_marketing%20-%20branding%20video.mp4";

// Hero images that will cycle
const HERO_IMAGES = [
  { src: MARKETING_PHOTO_3, alt: "Support that gets Me" },
  { src: MARKETING_PHOTO_4, alt: "My Kind of Independence" },
  { src: MARKETING_PHOTO_5, alt: "Support that gets Me — joyful moments with your worker" }
];

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

// Loading Screen Component
const LoadingScreen = ({ onLoadComplete }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onLoadComplete();
    }, 1200); // Faster loading screen
    return () => clearTimeout(timer);
  }, [onLoadComplete]);

  return (
    <motion.div 
      className="fixed inset-0 z-[100] bg-[#87CEEB] flex items-center justify-center"
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div 
        className="flex flex-col items-center"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.img 
          src={LOGO_ICON}
          alt="BTR Health"
          className="w-24 h-24 md:w-32 md:h-32"
          animate={{ rotate: 360 }}
          transition={{ 
            duration: 2,
            ease: "linear",
            repeat: Infinity
          }}
        />
        <motion.p 
          className="mt-6 text-white text-xl font-semibold tracking-wide"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          BTR Health
        </motion.p>
        <motion.div 
          className="mt-4 flex gap-1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-2 h-2 bg-white rounded-full"
              animate={{ 
                scale: [1, 1.5, 1],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 1,
                repeat: Infinity,
                delay: i * 0.2
              }}
            />
          ))}
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [videoMuted, setVideoMuted] = useState(true);
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [currentHeroImage, setCurrentHeroImage] = useState(0);
  const [currentWorker, setCurrentWorker] = useState(0);
  // Landing page lead form
  const [leadForm, setLeadForm] = useState({ name: "", phone: "", email: "", service_type: "", notes: "" });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSuccess, setLeadSuccess] = useState(false);
  const [leadError, setLeadError] = useState("");
  // Worker preview grid
  const [workers, setWorkers] = useState([]);
  // Hero search — effective but simple: location + gender + age + language
  const [heroSuburb, setHeroSuburb] = useState("");
  const [heroGender, setHeroGender] = useState("any");
  const [heroAge, setHeroAge] = useState("any");
  const [heroLanguage, setHeroLanguage] = useState("any");
  const videoRef = useRef(null);

  // Set page meta for SEO
  useEffect(() => {
    updatePageMeta(
      'BTR Health | Find NDIS Support Workers Australia - Zero Fees',
      'Find verified NDIS support workers across Australia. Zero fees for participants. Better matching than Mable or HireUp. Book care workers instantly.'
    );
  }, []);

  // Handle scroll for header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cycle hero images every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroImage((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch workers for the preview grid
  useEffect(() => {
    fetch(`${API}/workers`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setWorkers(Array.isArray(data) ? data : []))
      .catch(() => setWorkers([]));
  }, []);

  // Cycle worker carousel in the hero every 4 seconds (only when we have workers loaded)
  useEffect(() => {
    if (workers.length < 2) return;
    const interval = setInterval(() => {
      setCurrentWorker((prev) => (prev + 1) % Math.min(workers.length, 6));
    }, 4000);
    return () => clearInterval(interval);
  }, [workers.length]);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setVideoMuted(!videoMuted);
    }
  };

  const steps = [
    { icon: Search, title: "Search", desc: "Browse local support workers by location, skill, and availability.", num: "01" },
    { icon: Users, title: "Match", desc: "Review verified profiles, ratings, and find your perfect match.", num: "02" },
    { icon: Calendar, title: "Connect", desc: "Book directly, agree on times, and start receiving support.", num: "03" },
    { icon: Heart, title: "Thrive", desc: "Ongoing quality care with full transparency and NDIS compliance.", num: "04" }
  ];

  const stats = [
    { value: "100%", label: "Verified workers", icon: Shield },
    { value: "5.0", label: "Average Rating", icon: Star },
    { value: "100%", label: "Police Checked", icon: Award },
    { value: "<2hrs", label: "Response Time", icon: Clock }
  ];

  const charityStats = [
    { value: "5%", label: "Of every booking donated" },
    { value: "100%", label: "Causes we vet personally" }
  ];

  const charityPrograms = [
    { title: "Beyond Blue", stat: "Mental health", label: "Australia-wide", desc: "Supporting Australians experiencing depression, anxiety and mental health challenges. (beyondblue.org.au)" },
    { title: "Cerebral Palsy Alliance", stat: "Disability", label: "NSW & ACT", desc: "Research, therapy and equipment for people living with cerebral palsy. (cerebralpalsy.org.au)" },
    { title: "Vinnies Australia", stat: "Hardship", label: "Nationwide", desc: "Helping Australians experiencing homelessness, family hardship and financial crisis. (vinnies.org.au)" }
  ];

  const trustBadges = [
    "Registered NDIS Provider",
    "Police Checked Workers",
    "First Aid Certified",
    "Insurance Covered",
    "WWCC Verified",
    "NDIS Code of Conduct"
  ];

  return (
    <>
      <AnimatePresence>
        {loading && <LoadingScreen onLoadComplete={() => setLoading(false)} />}
      </AnimatePresence>

      <div className="min-h-screen bg-white overflow-x-hidden">
        {/* Header */}
        <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-[#87CEEB] shadow-lg' : 'bg-[#87CEEB]'
        }`}>
          <div className="container mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between h-16 md:h-20">
              <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
                <img src={LOGO_ICON} alt="BTR Health" className="h-8 md:h-10" />
                <span className="text-white font-bold text-xl md:text-2xl tracking-tight">BTR Health</span>
              </Link>

              {/* Desktop Nav */}
              <nav className="hidden md:flex items-center gap-8">
                <a href="#how-it-works" className="text-white/90 hover:text-white transition-colors font-medium">How It Works</a>
                <a href="#why-btr" className="text-white/90 hover:text-white transition-colors font-medium">Why BTR</a>
                <a href="#advisor" className="text-white/90 hover:text-white transition-colors font-medium">Get help</a>
              </nav>

              <div className="hidden md:flex items-center gap-3">
                <Button 
                  variant="ghost" 
                  className="text-white hover:bg-white/20 font-medium"
                  onClick={() => navigate("/worker-signup")}
                  data-testid="register-worker-btn"
                >
                  Join as Worker
                </Button>
                <Button 
                  className="bg-white text-[#87CEEB] hover:bg-white/90 rounded-full px-6 font-semibold shadow-lg"
                  onClick={() => navigate("/search")}
                  data-testid="get-started-btn"
                >
                  Find a Worker
                </Button>
              </div>

              {/* Mobile Menu Button */}
              <button 
                className="md:hidden p-2 text-white"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="md:hidden bg-[#6BB8DB] border-t border-white/20"
              >
                <div className="px-4 py-6">
                  <nav className="flex flex-col gap-4 mb-6">
                    <a href="#how-it-works" className="text-white py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>How It Works</a>
                    <a href="#why-btr" className="text-white py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>Why BTR</a>
                    <a href="#advisor" className="text-white py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>Get help</a>
                  </nav>
                  <div className="flex flex-col gap-3">
                    <Button variant="outline" onClick={() => { setMobileMenuOpen(false); navigate("/worker-signup"); }} className="w-full rounded-full bg-transparent border-white text-white hover:bg-white/20">Join as Worker</Button>
                    <Button className="w-full bg-white text-[#87CEEB] hover:bg-white/90 rounded-full font-semibold" onClick={() => { setMobileMenuOpen(false); navigate("/search"); }}>Find a Worker</Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </header>

        {/* Hero Section — conversion focused */}
        <section className="pt-20 sm:pt-24 md:pt-32 pb-10 sm:pb-14 md:pb-20 bg-gradient-to-b from-[#87CEEB]/5 via-white to-white">
          <div className="container mx-auto px-4 md:px-8">
            <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-center">
              {/* Left — text + search + CTAs */}
              <motion.div
                initial="initial"
                animate="animate"
                variants={stagger}
                className="lg:col-span-3 text-center lg:text-left"
              >
                <motion.div
                  variants={fadeInUp}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white shadow-sm border border-[#87CEEB]/40 text-[11px] sm:text-xs font-bold text-[#5BA3C0] uppercase tracking-wider mb-4"
                  data-testid="ndis-registered-badge"
                >
                  <Shield className="w-3.5 h-3.5" />
                  Registered NDIS Provider
                </motion.div>
                <motion.h1
                  variants={fadeInUp}
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 mb-3 sm:mb-4 leading-[1.02] tracking-tight"
                >
                  Find support<br />
                  <span className="text-[#87CEEB] italic font-extrabold">easy.</span>
                </motion.h1>

                {/* Simple 3-step value */}
                <motion.div
                  variants={fadeInUp}
                  className="flex items-center justify-center lg:justify-start gap-2 sm:gap-3 text-slate-700 font-semibold text-sm sm:text-base mb-1.5"
                >
                  <span className="flex items-center gap-1.5"><Search className="w-4 h-4 text-[#87CEEB]" /> Search</span>
                  <span className="text-slate-300">·</span>
                  <span className="flex items-center gap-1.5"><MessageCircle className="w-4 h-4 text-[#87CEEB]" /> Connect</span>
                  <span className="text-slate-300">·</span>
                  <span className="flex items-center gap-1.5"><Heart className="w-4 h-4 text-[#87CEEB]" /> Thrive</span>
                </motion.div>

                {/* Tagline */}
                <motion.p
                  variants={fadeInUp}
                  className="text-sm sm:text-base text-slate-500 mb-5 sm:mb-7"
                >
                  Search, book and manage support workers — all in one place.
                </motion.p>

                {/* SEARCH — primary CTA: location + gender + age + language */}
                <motion.form
                  variants={fadeInUp}
                  onSubmit={(e) => {
                    e.preventDefault();
                    const params = new URLSearchParams();
                    const q = heroSuburb.trim();
                    if (q) params.set("q", q);
                    if (heroGender && heroGender !== "any") params.set("gender", heroGender);
                    if (heroAge && heroAge !== "any") params.set("age", heroAge);
                    if (heroLanguage && heroLanguage !== "any") params.set("language", heroLanguage);
                    const qs = params.toString();
                    navigate(qs ? `/search?${qs}` : "/search");
                  }}
                  className="bg-white rounded-2xl border border-slate-200 shadow-lg shadow-slate-200/50 p-2 max-w-2xl mx-auto lg:mx-0 mb-4"
                  data-testid="hero-search-form"
                >
                  <div className="flex flex-col gap-2">
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 pointer-events-none" />
                      <Input
                        type="text"
                        placeholder="Suburb or postcode"
                        value={heroSuburb}
                        onChange={(e) => setHeroSuburb(e.target.value)}
                        className="pl-10 sm:pl-11 h-11 sm:h-12 border-0 bg-transparent text-sm sm:text-base focus-visible:ring-0 focus-visible:ring-offset-0"
                        data-testid="hero-search-input"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <select
                        value={heroGender}
                        onChange={(e) => setHeroGender(e.target.value)}
                        className="h-10 sm:h-11 rounded-lg border-0 bg-slate-50 px-2.5 text-xs sm:text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#87CEEB]"
                        data-testid="hero-search-gender"
                        aria-label="Worker gender preference"
                      >
                        <option value="any">Any gender</option>
                        <option value="female">Female</option>
                        <option value="male">Male</option>
                        <option value="non_binary">Non-binary</option>
                        <option value="no_preference">No preference</option>
                      </select>
                      <select
                        value={heroAge}
                        onChange={(e) => setHeroAge(e.target.value)}
                        className="h-10 sm:h-11 rounded-lg border-0 bg-slate-50 px-2.5 text-xs sm:text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#87CEEB]"
                        data-testid="hero-search-age"
                        aria-label="Participant age range"
                      >
                        <option value="any">Any age</option>
                        <option value="18-24">18–24</option>
                        <option value="25-34">25–34</option>
                        <option value="35-49">35–49</option>
                        <option value="50-64">50–64</option>
                        <option value="65+">65+</option>
                      </select>
                      <select
                        value={heroLanguage}
                        onChange={(e) => setHeroLanguage(e.target.value)}
                        className="h-10 sm:h-11 rounded-lg border-0 bg-slate-50 px-2.5 text-xs sm:text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#87CEEB]"
                        data-testid="hero-search-language"
                        aria-label="Preferred language"
                      >
                        <option value="any">Any language</option>
                        <option value="English">English</option>
                        <option value="Mandarin">Mandarin</option>
                        <option value="Cantonese">Cantonese</option>
                        <option value="Arabic">Arabic</option>
                        <option value="Vietnamese">Vietnamese</option>
                        <option value="Spanish">Spanish</option>
                        <option value="Hindi">Hindi</option>
                        <option value="Tagalog">Tagalog</option>
                        <option value="Auslan">Auslan (sign)</option>
                      </select>
                    </div>
                    <Button
                      type="submit"
                      onClick={(e) => {
                        e.preventDefault();
                        const params = new URLSearchParams();
                        const q = heroSuburb.trim();
                        if (q) params.set("q", q);
                        if (heroGender && heroGender !== "any") params.set("gender", heroGender);
                        if (heroAge && heroAge !== "any") params.set("age", heroAge);
                        if (heroLanguage && heroLanguage !== "any") params.set("language", heroLanguage);
                        const qs = params.toString();
                        navigate(qs ? `/search?${qs}` : "/search");
                      }}
                      className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white rounded-xl h-11 sm:h-12 text-sm sm:text-base font-semibold w-full"
                      data-testid="hero-search-submit"
                    >
                      <Search className="w-4 h-4 mr-1.5" />
                      Find Support
                    </Button>
                  </div>
                </motion.form>

                {/* Primary signup buttons — two clear paths */}
                <motion.div
                  variants={fadeInUp}
                  className="grid grid-cols-2 gap-2.5 sm:gap-3 max-w-xl mx-auto lg:mx-0"
                >
                  <Button
                    onClick={() => navigate("/search")}
                    className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white rounded-xl h-12 sm:h-13 text-sm sm:text-base font-bold shadow-md shadow-[#87CEEB]/30"
                    data-testid="hero-find-worker-btn"
                  >
                    Find a Support Worker
                  </Button>
                  <Button
                    onClick={() => navigate("/worker-signup")}
                    variant="outline"
                    className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-xl h-12 sm:h-13 text-sm sm:text-base font-bold"
                    data-testid="hero-signup-worker-btn"
                  >
                    Join as a Support Worker
                  </Button>
                </motion.div>

                {/* Mini trust bar */}
                <motion.div
                  variants={fadeInUp}
                  className="flex flex-wrap items-center gap-x-5 gap-y-2 justify-center lg:justify-start mt-6 text-xs sm:text-sm text-slate-600"
                >
                  <span className="flex items-center gap-1.5">
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                    <strong className="text-slate-900">5.0</strong> rated
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Shield className="w-4 h-4 text-emerald-600" />
                    <strong className="text-slate-900">100%</strong> verified
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Heart className="w-4 h-4 text-rose-500" />
                    <strong className="text-slate-900">$0</strong> participant fees
                  </span>
                </motion.div>
              </motion.div>

              {/* Right — worker profile carousel (visible on all viewports) */}
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="lg:col-span-2 mt-4 lg:mt-0"
                data-testid="hero-worker-carousel"
              >
                {(() => {
                  const pool = (workers || []).slice(0, 6);
                  const w = pool[currentWorker] || pool[0];
                  if (!w) {
                    // Skeleton while workers load
                    return (
                      <div className="w-full max-w-sm mx-auto bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100 aspect-[3/4]">
                        <div className="h-2/3 bg-slate-100 animate-pulse" />
                        <div className="p-5 space-y-2">
                          <div className="h-4 bg-slate-100 rounded animate-pulse w-2/3" />
                          <div className="h-3 bg-slate-100 rounded animate-pulse w-1/2" />
                        </div>
                      </div>
                    );
                  }
                  const langs = Array.isArray(w.languages)
                    ? w.languages
                    : (typeof w.languages === "string" ? w.languages.split(",").map(s => s.trim()).filter(Boolean) : []);
                  return (
                    <div className="relative w-full max-w-sm mx-auto">
                      {/* soft glow */}
                      <div className="absolute -inset-3 bg-gradient-to-br from-[#87CEEB]/30 via-transparent to-[#87CEEB]/10 rounded-[2rem] blur-2xl pointer-events-none" />

                      <AnimatePresence mode="wait">
                        <motion.button
                          key={w.worker_id}
                          type="button"
                          onClick={() => navigate(`/worker/${w.worker_id}`)}
                          initial={{ opacity: 0, y: 14, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -14, scale: 0.98 }}
                          transition={{ duration: 0.45, ease: "easeOut" }}
                          className="relative block w-full text-left bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100 hover:shadow-2xl hover:-translate-y-0.5 transition-all"
                          data-testid={`hero-worker-card-${w.worker_id}`}
                          aria-label={`View ${w.name}'s profile`}
                        >
                          {/* Photo */}
                          <div className="relative aspect-[4/3] sm:aspect-[5/4] bg-slate-100 overflow-hidden">
                            <img
                              src={w.picture}
                              alt={w.name}
                              className="absolute inset-0 w-full h-full object-cover"
                              style={{ objectPosition: typeof w.picture === "string" && w.picture.includes("ora_bun") ? "center 30%" : "center center" }}
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                            {w.verified && (
                              <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 text-[11px] font-bold text-emerald-700 shadow-sm">
                                <Shield className="w-3 h-3" /> Verified
                              </span>
                            )}
                            <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 text-[11px] font-bold text-slate-700 shadow-sm">
                              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" /> Available
                            </span>
                          </div>

                          {/* Info */}
                          <div className="p-4 sm:p-5">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-lg sm:text-xl font-bold text-slate-900 truncate">{w.name}</p>
                                <div className="flex items-center gap-1 text-slate-500 text-sm mt-0.5">
                                  <MapPin className="w-3.5 h-3.5 shrink-0" />
                                  <span className="truncate">{w.location}</span>
                                </div>
                              </div>
                              {w.review_count > 0 ? (
                                <div className="flex items-center gap-1 shrink-0">
                                  <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                                  <span className="font-bold text-slate-900 text-sm">{Number(w.rating).toFixed(1)}</span>
                                </div>
                              ) : (
                                <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold">
                                  <Sparkles className="w-3 h-3" /> New
                                </span>
                              )}
                            </div>

                            {/* Languages + first 2 skills */}
                            <div className="flex flex-wrap gap-1.5 mt-3">
                              {langs.slice(0, 2).map(l => (
                                <span key={l} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-[11px] font-medium">
                                  {l}
                                </span>
                              ))}
                              {(w.skills || []).slice(0, 2).map(s => (
                                <span key={s} className="px-2 py-0.5 bg-[#87CEEB]/15 text-[#5BA3C0] rounded-full text-[11px] font-semibold">
                                  {s}
                                </span>
                              ))}
                            </div>

                            <div className="mt-3 sm:mt-4 inline-flex items-center text-[13px] font-bold text-[#5BA3C0]">
                              View profile <ChevronRight className="w-4 h-4 ml-0.5" />
                            </div>
                          </div>
                        </motion.button>
                      </AnimatePresence>

                      {/* Dots */}
                      <div className="mt-4 flex items-center justify-center gap-2" role="tablist" aria-label="Featured workers">
                        {pool.map((pw, i) => (
                          <button
                            key={pw.worker_id}
                            type="button"
                            onClick={() => setCurrentWorker(i)}
                            className={`h-2 rounded-full transition-all ${i === currentWorker ? "bg-[#87CEEB] w-6" : "bg-slate-300 hover:bg-slate-400 w-2"}`}
                            aria-label={`Show ${pw.name}`}
                            aria-selected={i === currentWorker}
                            role="tab"
                          />
                        ))}
                      </div>
                      <p className="text-center text-[11px] text-slate-400 mt-2 hidden sm:block">
                        Meet some workers near you · tap to view full profile
                      </p>
                    </div>
                  );
                })()}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Trust / Benefits bar — sits between hero and workers */}
        <section className="bg-slate-50 border-y border-slate-100" data-testid="trust-bar">
          <div className="container mx-auto px-4 md:px-8 py-3 sm:py-4">
            <div className="max-w-6xl mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-3 gap-y-2.5 text-center sm:text-left">
              {[
                { icon: Search, label: "Search & book in minutes" },
                { icon: Calendar, label: "Manage shifts in one place" },
                { icon: Heart, label: "Lower rates than agencies" },
                { icon: Award, label: "Workers keep more" },
                { icon: CheckCircle2, label: "Full choice & control" },
                { icon: Shield, label: "Verified & police-checked" }
              ].map((b) => {
                const Icon = b.icon;
                return (
                  <div key={b.label} className="flex items-center justify-center sm:justify-start gap-2 min-w-0">
                    <Icon className="w-4 h-4 text-[#87CEEB] shrink-0" />
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-700 truncate">{b.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>


        {/* Worker Directory — compact preview under hero */}
        <section className="py-6 sm:py-8 md:py-10 bg-white border-t border-slate-100" id="workers">
          <div className="container mx-auto px-4 md:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-end justify-between gap-4 mb-4 sm:mb-5">
                <div>
                  <span className="text-[#87CEEB] font-semibold tracking-wide uppercase text-[11px] sm:text-xs">Available now near you</span>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-900 mt-0.5">Meet some of your local support workers</h2>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/search")}
                  className="hidden sm:inline-flex text-[#87CEEB] hover:text-[#5BA3C0]"
                  data-testid="landing-view-all-workers-top"
                >
                  Search all workers <ChevronRight className="ml-1 w-4 h-4" />
                </Button>
              </div>

              {/* Grid — compact cards */}
              {(() => {
                const filtered = workers;

                if (workers.length === 0) {
                  return <p className="text-center text-slate-400 py-6 text-sm">Loading workers…</p>;
                }
                if (filtered.length === 0) {
                  return <p className="text-center text-slate-500 py-6 text-sm">No workers available right now.</p>;
                }
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                    {filtered.map((w) => (
                      <Link
                        key={w.worker_id}
                        to={`/worker/${w.worker_id}`}
                        className="group bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-[#87CEEB]/40 transition-all duration-200 overflow-hidden"
                        data-testid={`landing-worker-${w.worker_id}`}
                      >
                        <div className="relative aspect-square bg-slate-100">
                          {w.picture ? (
                            <img
                              src={w.picture}
                              alt={w.name}
                              loading="lazy"
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          ) : null}
                          {w.verified && (
                            <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/95 backdrop-blur text-[10px] font-semibold text-emerald-700 shadow-sm">
                              <Shield className="w-2.5 h-2.5" /> Verified
                            </span>
                          )}
                        </div>
                        <div className="p-2.5 sm:p-3">
                          <div className="flex items-start justify-between gap-1.5">
                            <h3 className="font-semibold text-slate-900 text-sm leading-tight truncate">{w.name}</h3>
                            {w.review_count > 0 ? (
                              <div className="flex items-center gap-0.5 text-amber-500 shrink-0">
                                <Star className="w-3 h-3 fill-amber-400" />
                                <span className="text-xs font-semibold text-slate-700">{Number(w.rating).toFixed(1)}</span>
                              </div>
                            ) : (
                              <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full shrink-0">New</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-slate-500 text-[11px] mt-0.5 truncate">
                            <MapPin className="w-3 h-3 shrink-0" />
                            <span className="truncate">{w.location}{w.state ? `, ${w.state}` : ""}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                );
              })()}

              <div className="text-center mt-5 sm:mt-6">
                <Button
                  variant="outline"
                  onClick={() => navigate("/search")}
                  className="rounded-full px-6 border-2 border-slate-200 hover:border-[#87CEEB] hover:text-[#87CEEB]"
                  data-testid="landing-view-all-workers"
                >
                  Search all workers
                  <ChevronRight className="ml-1 w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works - Four Simple Steps with Visual Screenshots */}
        <section id="how-it-works" className="py-10 sm:py-14 md:py-20 bg-white">
          <div className="container mx-auto px-4 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="text-[#87CEEB] font-semibold tracking-wide uppercase text-sm">How It Works</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mt-3">
                Four Simple Steps
              </h2>
              <p className="text-lg text-slate-600 mt-4 max-w-2xl mx-auto">
                Finding the right support has never been easier
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 items-stretch">
              {/* Step 1: Search — mini search result with Chris */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0 }}
                className="relative group h-full"
              >
                <div className="h-full bg-slate-50 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-slate-100">
                  <div className="relative h-48 bg-gradient-to-br from-[#87CEEB]/20 to-[#87CEEB]/5 flex items-center justify-center">
                    <div className="w-full max-w-[200px] bg-white rounded-xl shadow-lg p-2.5 mx-4">
                      <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-100 rounded-lg mb-2">
                        <Search className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-[11px] text-slate-500">Chadstone, VIC</span>
                      </div>
                      <div className="flex items-center gap-2 p-1.5 bg-slate-50 rounded-lg border border-[#87CEEB]/40">
                        <img
                          src="/chris_ganesh.jpg"
                          alt="Chris Ganesh"
                          className="w-9 h-9 rounded-lg object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-bold text-slate-900 truncate">Chris Ganesh</p>
                          <p className="text-[10px] text-slate-500 truncate">Chadstone, VIC</p>
                        </div>
                        <span className="text-[9px] font-semibold text-emerald-700 bg-emerald-50 px-1 py-0.5 rounded">New</span>
                      </div>
                    </div>
                    <span className="absolute top-4 right-4 text-6xl font-bold text-[#87CEEB]/20">01</span>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Search</h3>
                    <p className="text-slate-600 leading-relaxed text-sm">Browse local support workers by location, skill, and availability using our powerful search.</p>
                  </div>
                </div>
              </motion.div>

              {/* Step 2: Match — Chris's full profile preview */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="relative group h-full"
              >
                <div className="h-full bg-slate-50 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-slate-100">
                  <div className="relative h-48 bg-gradient-to-br from-[#87CEEB]/20 to-[#87CEEB]/5 flex items-center justify-center">
                    <div className="w-[180px] bg-white rounded-xl shadow-xl overflow-hidden border-2 border-[#87CEEB] mx-4">
                      <div className="relative h-20 bg-slate-100">
                        <img
                          src="/chris_ganesh.jpg"
                          alt="Chris Ganesh"
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-white/95 text-[8px] font-semibold text-emerald-700 shadow-sm">
                          <Shield className="w-2 h-2" /> Verified
                        </span>
                      </div>
                      <div className="p-2">
                        <div className="flex items-center justify-between gap-1">
                          <p className="text-[11px] font-bold text-slate-900 truncate">Chris Ganesh</p>
                          <span className="text-[9px] font-semibold text-emerald-700 bg-emerald-50 px-1 py-0.5 rounded shrink-0">New</span>
                        </div>
                        <div className="flex items-center gap-1 mt-0.5 text-slate-500">
                          <MapPin className="w-2.5 h-2.5" />
                          <span className="text-[10px]">Chadstone, VIC</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          <span className="px-1.5 py-0.5 bg-[#87CEEB]/10 text-[#5BA3C0] text-[9px] font-semibold rounded-full">Community</span>
                          <span className="px-1.5 py-0.5 bg-[#87CEEB]/10 text-[#5BA3C0] text-[9px] font-semibold rounded-full">Transport</span>
                        </div>
                      </div>
                    </div>
                    <span className="absolute top-4 right-4 text-6xl font-bold text-[#87CEEB]/20">02</span>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Match</h3>
                    <p className="text-slate-600 leading-relaxed text-sm">Review verified profiles, ratings, and reviews to find the perfect match for your needs.</p>
                  </div>
                </div>
              </motion.div>

              {/* Step 3: Connect */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="relative group h-full"
              >
                <div className="h-full bg-slate-50 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-slate-100">
                  <div className="relative h-48 bg-gradient-to-br from-[#87CEEB]/20 to-[#87CEEB]/5 flex items-center justify-center">
                    <div className="w-full max-w-[200px] bg-white rounded-xl shadow-lg p-3 mx-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 bg-[#87CEEB] rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">R</span>
                        </div>
                        <div>
                          <p className="text-xs font-semibold">Rashaun Peter</p>
                          <p className="text-[10px] text-slate-400">Online now</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="bg-slate-100 rounded-lg rounded-tl-none p-2 text-xs text-slate-600 max-w-[80%]">
                          Hi! I'd love to help you
                        </div>
                        <div className="bg-[#87CEEB] rounded-lg rounded-tr-none p-2 text-xs text-white ml-auto max-w-[80%]">
                          When are you available?
                        </div>
                      </div>
                    </div>
                    <span className="absolute top-4 right-4 text-6xl font-bold text-[#87CEEB]/20">03</span>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Connect</h3>
                    <p className="text-slate-600 leading-relaxed text-sm">Message workers directly, discuss your needs, and build a connection before booking.</p>
                  </div>
                </div>
              </motion.div>

              {/* Step 4: Thrive */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="relative group h-full"
              >
                <div className="h-full bg-slate-50 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-slate-100">
                  <div className="relative h-48 bg-gradient-to-br from-[#87CEEB]/20 to-[#87CEEB]/5 flex items-center justify-center">
                    <div className="w-full max-w-[200px] mx-4">
                      <div className="bg-white rounded-xl shadow-lg p-4 mb-2">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-[#87CEEB] to-[#6BB8DB] rounded-full flex items-center justify-center">
                            <Heart className="w-5 h-5 text-white fill-white" />
                          </div>
                          <div>
                            <p className="text-xs font-bold text-slate-900">Premium Support</p>
                            <p className="text-[10px] text-emerald-500 font-medium">Active</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                            <span className="text-[10px] text-slate-600">24/7 Care Coordination</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                            <span className="text-[10px] text-slate-600">NDIS Plan Management</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                            <span className="text-[10px] text-slate-600">Priority Matching</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <span className="absolute top-4 right-4 text-6xl font-bold text-[#87CEEB]/20">04</span>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Thrive</h3>
                    <p className="text-slate-600 leading-relaxed text-sm">Ongoing premium support with care coordination, NDIS compliance, and dedicated assistance every step of the way.</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Why BTR + Brand Video (merged) */}
        <section id="why-btr" className="py-10 sm:py-14 md:py-20 bg-gradient-to-br from-slate-50 to-white">
          <div className="container mx-auto px-4 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center mb-8 md:mb-12"
            >
              <span className="text-[#87CEEB] font-semibold tracking-wide uppercase text-xs sm:text-sm">Why BTR Health</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mt-2 mb-3 leading-tight">
                A better way than the <span className="text-slate-400 line-through">traditional agency</span>
              </h2>
              <p className="text-base sm:text-lg text-slate-600">
                Old-school NDIS agencies are slow, expensive and impersonal. BTR Health is built around you.
              </p>
            </motion.div>

            {/* Brand video on top */}
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-2xl mb-8 md:mb-12 bg-slate-900"
            >
              <video
                ref={videoRef}
                className="w-full h-auto"
                autoPlay
                muted
                loop
                playsInline
              >
                <source src={BRAND_VIDEO} type="video/mp4" />
              </video>
              <button
                onClick={toggleMute}
                className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors"
                data-testid="mute-video-btn"
                aria-label={videoMuted ? "Unmute video" : "Mute video"}
              >
                {videoMuted ? <VolumeX className="w-4 h-4 sm:w-5 sm:h-5 text-white" /> : <Volume2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />}
              </button>
            </motion.div>

            {/* Comparison cards */}
            <div className="grid md:grid-cols-2 gap-5 sm:gap-6 max-w-5xl mx-auto" data-testid="why-btr-comparison">
              {/* Old way */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-3xl p-7 md:p-8 border border-slate-200 shadow-sm"
              >
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-2">Traditional agency</p>
                <h3 className="text-xl md:text-2xl font-bold text-slate-700 mb-5">The old way</h3>
                <ul className="space-y-3.5 text-slate-600">
                  {[
                    "Allocated to whichever worker is on shift",
                    "Locked into long contracts and minimum hours",
                    "Marked-up rates with hidden admin fees",
                    "Slow phone trees, days for a callback",
                    "One-size-fits-all support",
                    "No control over who turns up at your door",
                  ].map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
              {/* BTR way */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative bg-gradient-to-br from-[#87CEEB] to-[#6BB8DB] text-white rounded-3xl p-7 md:p-8 shadow-xl shadow-[#87CEEB]/30"
              >
                <div className="absolute -top-3 right-7 bg-amber-400 text-slate-900 px-3 py-1 rounded-full text-xs font-bold shadow">RECOMMENDED</div>
                <p className="text-xs uppercase tracking-wider font-semibold text-white/80 mb-2">BTR Health</p>
                <h3 className="text-xl md:text-2xl font-bold mb-5">The new way</h3>
                <ul className="space-y-3.5">
                  {[
                    "Choose the worker that fits you — see their bio, photo, languages",
                    "Book one shift, ten, or none — zero lock-in",
                    "Transparent pricing aligned with your NDIS plan",
                    "Real humans on chat or phone, replies in under 2 hours",
                    "Support tailored to your routine, your goals, your way",
                    "You're in control — message, book, cancel any time",
                  ].map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-white mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/></svg>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  size="lg"
                  className="mt-7 w-full bg-white text-[#87CEEB] hover:bg-white/90 rounded-full font-semibold h-12"
                  onClick={() => navigate("/search")}
                  data-testid="why-btr-cta"
                >
                  Find a support worker
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Support Advisor Lead Form */}
        <section id="advisor" className="py-10 sm:py-14 md:py-20 bg-white">
          <div className="container mx-auto px-4 md:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <span className="text-[#87CEEB] font-semibold tracking-wide uppercase text-sm">Prefer we do the searching?</span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mt-3 mb-5 leading-tight">
                  Let one of our <span className="text-[#87CEEB]">support advisors</span> call you back
                </h2>
                <p className="text-lg text-slate-600 mb-5 leading-relaxed">
                  Tell us a few quick details and a real person from BTR Health will call within 1 business day. We'll listen to your situation, answer questions and find the right support worker for you — no commitment, no fees.
                </p>
                <ul className="space-y-2.5 mb-2">
                  {[
                    "Free, no-obligation chat",
                    "Australian team — real humans, not bots",
                    "We do the searching for you",
                  ].map((p) => (
                    <li key={p} className="flex items-center gap-2 text-slate-700">
                      <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/></svg>
                      </span>
                      {p}
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.form
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (leadSubmitting) return;
                  if (!leadForm.name || !leadForm.phone) {
                    setLeadError("Please fill in your name and phone");
                    return;
                  }
                  setLeadSubmitting(true);
                  setLeadError("");
                  try {
                    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/support-requests`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify(leadForm),
                    });
                    if (r.ok) {
                      setLeadSuccess(true);
                      setLeadForm({ name: "", phone: "", email: "", service_type: "", notes: "" });
                    } else {
                      setLeadError("Something went wrong. Please try again or call us.");
                    }
                  } catch (err) {
                    setLeadError("Network error. Please try again.");
                  } finally {
                    setLeadSubmitting(false);
                  }
                }}
                className="bg-white rounded-3xl border border-slate-200 shadow-xl p-6 md:p-8 space-y-4"
                data-testid="landing-lead-form"
              >
                {leadSuccess ? (
                  <div className="text-center py-10" data-testid="lead-success">
                    <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                      <svg className="w-7 h-7 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/></svg>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-1">Thanks — we've got it</h3>
                    <p className="text-slate-600">A BTR Health support advisor will call you within 1 business day.</p>
                    <button
                      type="button"
                      onClick={() => setLeadSuccess(false)}
                      className="mt-5 text-sm text-[#87CEEB] hover:underline"
                    >
                      Submit another request
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="text-center mb-2">
                      <h3 className="text-xl font-bold text-slate-900">Request a callback</h3>
                      <p className="text-sm text-slate-500">Takes 30 seconds — no account needed.</p>
                    </div>
                    <input
                      type="text"
                      placeholder="Your name *"
                      value={leadForm.name}
                      onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                      className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:border-[#87CEEB] focus:outline-none focus:ring-2 focus:ring-[#87CEEB]/30"
                      data-testid="lead-name"
                      required
                    />
                    <input
                      type="tel"
                      placeholder="Phone number *"
                      value={leadForm.phone}
                      onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                      className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:border-[#87CEEB] focus:outline-none focus:ring-2 focus:ring-[#87CEEB]/30"
                      data-testid="lead-phone"
                      required
                    />
                    <input
                      type="email"
                      placeholder="Email (optional)"
                      value={leadForm.email}
                      onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                      className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:border-[#87CEEB] focus:outline-none focus:ring-2 focus:ring-[#87CEEB]/30"
                      data-testid="lead-email"
                    />
                    <select
                      value={leadForm.service_type}
                      onChange={(e) => setLeadForm({ ...leadForm, service_type: e.target.value })}
                      className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:border-[#87CEEB] focus:outline-none focus:ring-2 focus:ring-[#87CEEB]/30 bg-white text-slate-700"
                      data-testid="lead-service"
                    >
                      <option value="">What support do you need?</option>
                      <option value="Personal Care">Personal Care</option>
                      <option value="Domestic Assistance">Domestic Assistance</option>
                      <option value="Community Access">Community Access</option>
                      <option value="Transport">Transport</option>
                      <option value="Therapy Support">Therapy Support</option>
                      <option value="Not sure — please advise">Not sure — please advise</option>
                    </select>
                    <textarea
                      placeholder="Anything else we should know? (optional)"
                      value={leadForm.notes}
                      onChange={(e) => setLeadForm({ ...leadForm, notes: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#87CEEB] focus:outline-none focus:ring-2 focus:ring-[#87CEEB]/30 resize-none"
                      data-testid="lead-notes"
                    />
                    {leadError && (
                      <p className="text-sm text-red-600">{leadError}</p>
                    )}
                    <Button
                      type="submit"
                      disabled={leadSubmitting}
                      className="w-full h-12 bg-[#87CEEB] hover:bg-[#6BB8DB] text-white rounded-full font-semibold text-base"
                      data-testid="lead-submit"
                    >
                      {leadSubmitting ? "Sending…" : "Request callback"}
                    </Button>
                    <p className="text-xs text-center text-slate-400">
                      We'll only use your details to contact you about your request.
                    </p>
                  </>
                )}
              </motion.form>
            </div>
          </div>
        </section>

        {/* Trust + Final CTA (combined, compact) */}
        <section id="about" className="py-10 sm:py-14 md:py-20 bg-gradient-to-b from-white to-[#87CEEB]/10">
          <div className="container mx-auto px-4 md:px-8">
            {/* Stats Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-5xl mx-auto"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl p-4 sm:p-5 text-center border border-slate-100 shadow-sm"
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#87CEEB]/15 flex items-center justify-center mx-auto mb-2 sm:mb-3">
                    <stat.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${stat.icon === Star ? 'text-amber-500 fill-amber-500' : 'text-[#87CEEB]'}`} />
                  </div>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900">{stat.value}</p>
                  <p className="text-slate-600 text-xs sm:text-sm mt-1">{stat.label}</p>
                </motion.div>
              ))}
            </motion.div>

            {/* Trust Badges (compact, single line on desktop) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex flex-wrap justify-center gap-2 mt-6 sm:mt-8"
            >
              {trustBadges.map((badge) => (
                <span key={badge} className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-xs sm:text-sm font-medium flex items-center gap-1.5 border border-emerald-100">
                  <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {badge}
                </span>
              ))}
            </motion.div>

            {/* Final CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-10 sm:mt-14 text-center max-w-2xl mx-auto"
            >
              <img src={LOGO_ICON} alt="BTR Health" className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-3 sm:mb-4" />
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3">
                Ready to Find Your Support?
              </h2>
              <p className="text-base sm:text-lg text-slate-600 mb-6">
                Join the BTR Health community and meet your next support worker.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  size="lg"
                  className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white rounded-full px-6 sm:px-8 h-12 text-base font-semibold shadow-xl shadow-[#87CEEB]/30"
                  onClick={() => navigate("/search")}
                  data-testid="cta-get-started-btn"
                >
                  Browse Workers
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-6 sm:px-8 h-12 text-base border-2 border-slate-200 text-slate-700 hover:border-[#87CEEB] hover:text-[#87CEEB]"
                  onClick={() => navigate("/worker-signup")}
                >
                  Join as a Support Worker
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 bg-slate-900 text-white">
          <div className="container mx-auto px-4 md:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <img src={LOGO_ICON} alt="BTR Health" className="h-8" />
                  <span className="font-bold text-lg">BTR Health</span>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Connecting NDIS participants with trusted local support workers across Australia.
                </p>
              </div>
              <div>
                <h4 className="font-bold mb-4">Platform</h4>
                <ul className="space-y-2 text-slate-400 text-sm">
                  <li><a href="/search" className="hover:text-[#87CEEB] transition-colors">Find Support</a></li>
                  <li><a href="/how-it-works/participants" className="hover:text-[#87CEEB] transition-colors">How it works (Participants)</a></li>
                  <li><a href="/how-it-works/workers" className="hover:text-[#87CEEB] transition-colors">How it works (Workers)</a></li>
                  <li><a href="/worker-signup" className="hover:text-[#87CEEB] transition-colors">Become a Worker</a></li>
                  <li><a href="/pricing" className="hover:text-[#87CEEB] transition-colors">Pricing</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold mb-4">Trust & Help</h4>
                <ul className="space-y-2 text-slate-400 text-sm">
                  <li><a href="/safety" className="hover:text-[#87CEEB] transition-colors">Safety & Insurance</a></li>
                  <li><a href="/help" className="hover:text-[#87CEEB] transition-colors">Help center</a></li>
                  <li><a href="/report-incident" className="hover:text-[#87CEEB] transition-colors">Report an incident</a></li>
                  <li><a href="mailto:hello@btrhealth.com.au" className="hover:text-[#87CEEB] transition-colors">Contact</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold mb-4">Get the App</h4>
                <p className="text-slate-400 text-xs mb-3">Coming soon to iOS and Android</p>
                <div className="flex flex-col gap-2">
                  <button type="button" disabled className="flex items-center gap-2 bg-slate-800 text-white rounded-xl px-3 py-2 cursor-not-allowed text-left">
                    <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                    <span><span className="block text-[9px] uppercase tracking-wider text-slate-400">Soon · iOS</span><span className="block text-sm font-semibold">App Store</span></span>
                  </button>
                  <button type="button" disabled className="flex items-center gap-2 bg-slate-800 text-white rounded-xl px-3 py-2 cursor-not-allowed text-left">
                    <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none"><path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92z" fill="#34a853"/><path d="M16.81 15.012l-2.732-2.732 2.731-2.732 3.557 2.025c.832.473.832 1.673 0 2.146l-3.557 2.293z" fill="#fbbc04"/><path d="M14.078 12.28l-10.469 9.906c.317.121.69.115 1.018-.07l11.183-6.41-1.732-3.426z" fill="#ea4335"/><path d="M14.078 12.28L15.81 8.854 4.627 1.884a.999.999 0 00-1.018-.07l10.469 9.466z" fill="#4285f4"/></svg>
                    <span><span className="block text-[9px] uppercase tracking-wider text-slate-400">Soon · Android</span><span className="block text-sm font-semibold">Google Play</span></span>
                  </button>
                </div>
              </div>
              <div>
                <h4 className="font-bold mb-4">Legal</h4>
                <ul className="space-y-2 text-slate-400 text-sm">
                  <li><Link to="/privacy" className="hover:text-[#87CEEB] transition-colors">Privacy Policy</Link></li>
                  <li><Link to="/terms" className="hover:text-[#87CEEB] transition-colors">Terms of Service</Link></li>
                  <li><Link to="/contact" className="hover:text-[#87CEEB] transition-colors">Contact Us</Link></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-slate-400 text-sm">&copy; 2024 BTR Health. All rights reserved.</p>
              <p className="text-slate-400 text-sm">Operating under the NDIS Code of Conduct</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
